import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Ctx = createContext(null);
export const useDraft = () => useContext(Ctx);

export function DraftProvider({ draft, children }) {
  const qc = useQueryClient();
  const [reuse, setReuse] = useState(null);
  const id = draft.draft_id;
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: ["imports", id] }), [qc, id]);

  const assign = useCallback(async (itemId, face, fileId, confirm = false) => {
    try {
      await api.put(`/imports/${id}/items/${itemId}/faces/${face}`, { file_id: fileId, confirm_reuse: confirm });
      refresh();
    } catch (err) {
      const msg = formatApiError(err);
      if (err.response?.status === 409 && msg.startsWith("REUSE_CONFIRMATION")) {
        setReuse({ msg: msg.replace("REUSE_CONFIRMATION: ", ""), args: [itemId, face, fileId] });
      } else toast.error(msg);
    }
  }, [id, refresh]);

  const value = useMemo(() => ({ draft, id, refresh, assign, open: draft.status === "draft" }), [draft, id, refresh, assign]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <AlertDialog open={Boolean(reuse)} onOpenChange={(o) => !o && setReuse(null)}>
        <AlertDialogContent className="border-[#2A3447] bg-[#121620] text-slate-200" data-testid="import-reuse-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-amber-100">Use this picture again?</AlertDialogTitle>
            <AlertDialogDescription>{reuse?.msg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-reuse-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { assign(...reuse.args, true); setReuse(null); }} data-testid="import-reuse-confirm">Reuse it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}
